<template>
	<label
		class="SelectItem"
		:class="{ disabled: item.state === SelectListItemStateType.DISABLED }"
		:for="String(item.value)"
		@click="handleLabelClick"
	>
		<div class="ContentText">
			<slot name="icon"></slot>

			<span
				class="Text"
				:class="{
					'SubItemText': isSubItem,
					'SelectedText': highlightSelectedText && item.state === SelectListItemStateType.SELECTED && !isSubItem,
					'SelectedSubItemText': highlightSelectedText && item.state === SelectListItemStateType.SELECTED && isSubItem
				}"
				:id="`${item.value}-label`"
			>{{ item.text }}</span>
		</div>

		<div class="ContentInput">
			<span
				v-if="item.count !== undefined"
				class="Count"
				aria-hidden="true"
			>{{ formatCount(item.count) }}</span>

			<input
				:checked="item.state === SelectListItemStateType.SELECTED"
				:id="String(item.value)"
				:type="type"
				:value="item.value"
				:name="name"
				:disabled="item.state === SelectListItemStateType.DISABLED"
				:aria-label="`${item.value}-label`"
				:aria-checked="item.state === SelectListItemStateType.SELECTED"
				:aria-disabled="item.state === SelectListItemStateType.DISABLED"
				class="Input"
				:class="{ 'PartiallySelected': partiallySelected }"
				ref="inputRef"
				@change="handleChange"
			/>
		</div>
	</label>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue';
import ISelectItem from '@app/interfaces/dtos/ISelectItem';
import type { SelectListItemType } from "@app/enums/SelectListItemType";
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import { formatCount } from '@utils/formatCount';

const props = defineProps({
	item: {
		type: Object as PropType<ISelectItem>,
		required: true,
	},
	type: {
		type: String as PropType<SelectListItemType>,
		required: true,
	},
	isSubItem: {
		type: Boolean,
		required: false,
		default: false,
	},
	name: {
		type: String,
		required: false,
		default: undefined,
	},
	partiallySelected: {
		type: Boolean,
		required: false,
		default: false,
	},
	highlightSelectedText: {
		type: Boolean,
		required: false,
		default: false,
	}
});

const emit = defineEmits<{
	'selectionChange': [state: string];
}>();

const inputRef = ref<HTMLInputElement | null>(null);

const handleChange = (event: Event) => {
	const target = event.target as HTMLInputElement;
	emit('selectionChange', target.checked ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED);
};

const handleLabelClick = (event: MouseEvent) => {
	const target = event.target as HTMLElement;
	if (target.closest('input')) return;
	if (inputRef.value?.disabled) return;

	event.preventDefault();
	inputRef.value?.click();
};

</script>

<style scoped lang="scss">
.SelectItem {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	gap: 0.5rem;
	cursor: pointer;

	.ContentText {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		position: relative;
		flex: 1;
		overflow: hidden;
		min-width: 0;

		.Icon {
			width: 1rem;
			height: 1rem;
			object-fit: contain;
			flex-shrink: 0;
		}

		.Text {
			user-select: none;
			color: $Grey-900;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			min-width: 0;
			flex: 1;
			max-width: 100%;
			@include TextStyle(BodyRegular);

			&.SubItemText {
				@include TextStyle(BodyMediumRegular);
			}

			&.SelectedText {
				@include TextStyle(BodySemibold);
			}

			&.SelectedSubItemText {
				@include TextStyle(BodyMediumSemibold);
			}
		}
	}

	.ContentInput {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;

		.Input {
			appearance: none;
			-webkit-appearance: none;
			-moz-appearance: none;
			width: 1.25rem;
			height: 1.25rem;
			border: 1px solid $Grey-600;
			margin: 0;
			flex-shrink: 0;
			transition: border 0.2s ease, border-color 0.2s ease, border-width 0.2s ease, background-color 0.2s ease, outline 0s ease;
			background-color: white;
			cursor: pointer;

			&[type='radio'] {
				border-radius: 50%;

				&:checked {
					border-color: $Blue-700;
					border-width: 0.25rem;
				}
			}

			&[type='checkbox'] {
				border-radius: 0.25rem;
				position: relative;

				&:checked {
					background-color: $Blue-700;
					border-color: $Blue-700;

					&::after {
						content: '';
						position: absolute;
						left: 0.4rem;
						top: 0.1rem;
						width: 0.3rem;
						height: 0.7rem;
						border: solid white;
						border-width: 0 1px 1px 0;
						transform: rotate(45deg);
					}

					&.PartiallySelected::after {
						left: 0.2rem;
						top: 0.55rem;
						width: 0.75rem;
						height: 0;
						border-width: 0 0 1px 0;
						transform: none;
					}
				}
			}

			&:disabled {
				cursor: not-allowed;
			}
		}

		.Count {
			color: $Grey-900;
			white-space: nowrap;
			flex-shrink: 0;
			user-select: none;
			@include TextStyle(BodySmallRegular);
		}
	}

	&.disabled {
		cursor: not-allowed;

		.Text {
			color: $Grey-600;
		}

		.Count {
			color: $Grey-600;
		}

		.Input {
			border-color: $Grey-600;
			background-color: $Grey-100;
		}
	}
}
</style>
